/* eslint-disable max-lines */
/*
 * AI IMAGE SERVICE - Rails Backend Proxy
 *
 * Calls Rails API endpoint which proxies to Replicate (avoids CORS issues)
 */

import type { GeneratedImage, GenerateImageParams, UploadResponse } from "./types";

class AIImageService {
  private apiUrl = "/api/ai_images/generate";

  private refineUrl = "/api/ai_images/refine";

  private uploadUrl = "/api/ai_images/upload";

  async generateImages({
    prompt,
    selectedTemplate,
    selectedStyle,
    selectedOrientation,
    selectedColors
  }: GenerateImageParams): Promise<GeneratedImage[]> {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await fetch(this.apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          prompt,
          selected_template: selectedTemplate,
          selected_style: selectedStyle,
          selected_orientation: selectedOrientation,
          selected_colors: selectedColors
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Image generation failed: ${response.status} - ${errorData.error || "Unknown error"}`
        );
      }

      const data = await response.json();
      if (data.images && Array.isArray(data.images)) {
        return data.images.map((image: any) => ({
          imageVersionId: image.ai_image_version_id?.toString() || "",
          url: image.url,
          provider: image.provider || ""
        }));
      }

      // If all else fails, log the full response and throw error
      throw new Error("Invalid response format from server - check console for details");
    } catch (error) {
      throw error;
    }
  }

  async refineImage({
    refinementPrompt,
    inputImageUrl
  }: {
    refinementPrompt: string;
    inputImageUrl: string;
  }): Promise<GeneratedImage> {
    try {
      const response = await fetch(this.refineUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          refinement_prompt: refinementPrompt,
          input_image_url: inputImageUrl
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Image refinement failed: ${response.status} - ${errorData.error || "Unknown error"}`
        );
      }

      const data = await response.json();

      return {
        imageVersionId: data.ai_image_version_id?.toString() || "",
        url: data.url,
        provider: data.provider || ""
      };
    } catch (error) {
      console.error("Error refining image:", error);
      throw error;
    }
  }

  async uploadImage(file: File): Promise<UploadResponse> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(this.uploadUrl, {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Image upload failed: ${response.status} - ${errorData.error || "Unknown error"}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  }
}

// Export singleton instance
export const aiImageService = new AIImageService();
